// src/components/MapWithDrawing/mapUtils.js
import L from "leaflet";

// Helper function to load Esri Leaflet libraries dynamically
export const loadEsriLeaflet = () => {
    // Check if already loaded
    if (window.L && window.L.esri && window.L.esri.dynamicMapLayer) {
        console.log("Esri Leaflet already available.");
        return Promise.resolve();
    }
    console.log("Loading Esri Leaflet from CDN...");
    const loadScript = (src) => new Promise((resolve, reject) => {
        // Avoid adding script if it already exists
        if (document.querySelector(`script[src="${src}"]`)) {
            console.log(`Script ${src} already added to head.`);
            // Attempt to resolve assuming it loaded or will load
            // A more robust check might be needed if load order matters critically
            return resolve();
        }
        const script = document.createElement('script');
        script.src = src; script.async = true; script.onload = resolve; script.onerror = reject;
        document.head.appendChild(script);
    });

    // Load main library first
    return loadScript("https://unpkg.com/esri-leaflet@3.0.10/dist/esri-leaflet.js")
        .then(() => {
            console.log("Esri Leaflet main library potentially loaded.");
            // Check for dynamicMapLayer after loading
            if (!window.L?.esri?.dynamicMapLayer) {
                console.warn("L.esri.dynamicMapLayer not found after loading esri-leaflet. Dynamic layers might require additional plugins or a specific library version.");
            }
        })
        .catch(error => {
            console.error("Failed to load Esri Leaflet:", error);
            // Don't throw, allow app to proceed without dynamic layer support
        });
};

// Check status and details of a Maricopa MapServer
export const checkYearService = async (year) => {
    try {
        const serviceUrl = `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Aerials${year}/MapServer`;
        // Use a timeout to prevent hanging indefinitely
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await fetch(`${serviceUrl}?f=pjson`, { signal: controller.signal });
        clearTimeout(timeoutId); // Clear timeout if fetch completes

        if (!response.ok) {
            console.warn(`Service for ${year} not found or failed: ${response.status} ${response.statusText}`);
            return null;
        }
        const data = await response.json();
        console.log(`${year} service data received.`);

        // --- Simplified parsing logic (same as before) ---
        const isExplicitlyTiled = data.singleFusedMapCache === true;
        const isPotentiallyDynamicCandidate = year < 2020; // Adjust year threshold if needed

        let maxZoom = 21, minZoom = 0, scales = [], origin = null, bounds = null, tileFormat = null;
        let wkid = data.spatialReference?.wkid || 102100;

        if (data.tileInfo?.lods?.length > 0) {
            minZoom = data.tileInfo.lods[0].level;
            maxZoom = data.tileInfo.lods[data.tileInfo.lods.length - 1].level;
            scales = data.tileInfo.lods.map(lod => ({ level: lod.level, scale: lod.scale, resolution: lod.resolution }));
            origin = data.tileInfo.origin;
            tileFormat = data.tileInfo.format;
            console.log(`${year}: Found tileInfo. MinZoom: ${minZoom}, MaxZoom: ${maxZoom}`);
        } else {
            console.log(`${year}: No detailed tileInfo found. Using defaults.`);
            if (data.fullExtent) maxZoom = 21; // Use higher maxZoom if likely vector/dynamic
        }

        if (data.fullExtent && L.Projection.SphericalMercator.unproject) {
            try {
                bounds = L.latLngBounds(
                    L.Projection.SphericalMercator.unproject({x: data.fullExtent.xmin, y: data.fullExtent.ymin}),
                    L.Projection.SphericalMercator.unproject({x: data.fullExtent.xmax, y: data.fullExtent.ymax})
                );
            } catch (projError) { console.warn(`Error unprojecting bounds for ${year}:`, projError); }
        }

        const supportsExport = (data.capabilities || "").includes("Map");
        // Use dynamic IF: It's an older candidate AND it's NOT explicitly tiled AND it supports export
        const useDynamic = isPotentiallyDynamicCandidate && !isExplicitlyTiled && supportsExport;

        let url = `${serviceUrl}/tile/{z}/{y}/{x}`;
        let isExportService = false;

         if (useDynamic) {
             url = serviceUrl; // Base URL for dynamic layer attempt
             isExportService = true;
             console.log(`${year}: Identified as candidate for DynamicMapLayer attempt.`);
         } else {
              console.log(`${year}: Using TileLayer approach. Explicitly Tiled: ${isExplicitlyTiled}, Supports Export: ${supportsExport}`);
         }

        return {
            year, name: `Maricopa County ${year}`, displayName: `Maricopa County (${year})`,
            url, serviceUrl, attribution: data.copyrightText || `© Maricopa County Assessor (${year})`,
            maxZoom, minZoom, wkid, description: data.serviceDescription || data.description || "",
            isTiledService: isExplicitlyTiled, isExportService,
            tileFormat, origin, scales, bounds,
            supportsDynamicLayers: data.supportsDynamicLayers === true, supportsExport,
            group: "maricopa"
        };
    } catch (error) {
        if (error.name === 'AbortError') {
            console.warn(`Timeout checking service for ${year}.`);
        } else {
            console.error(`Error checking ${year} service URL:`, error);
        }
        return null; // Indicate service check failed or timed out
    }
};

// Define Google Maps providers (could also be fetched from config)
export const googleMapsProvider = { name: "Google Maps", displayName: "Google Maps", url: "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", attribution: "© Google Maps", maxZoom: 20, minZoom: 0, group: "google" };
export const googleSatelliteProvider = { name: "Google Satellite", displayName: "Google Satellite", url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", attribution: "© Google Maps", maxZoom: 20, minZoom: 0, group: "google" };
export const googleHybridProvider = { name: "Google Hybrid", displayName: "Google Hybrid", url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", attribution: "© Google Maps", maxZoom: 20, minZoom: 0, group: "google" };

// Default style for drawings
export const defaultStyle = {
    fillColor: '#3388ff',
    fillOpacity: 0.4,
    color: '#3388ff', // Stroke color
    weight: 3, // Stroke width
    opacity: 0.8 // Stroke opacity
};

/**
 * Utility to generate a unique ID for drawn objects
 * @returns {string} - A unique identifier
 */
export const generateUniqueId = () => {
    return 'id_' + Math.random().toString(36).substr(2, 9);
};

/**
 * Calculates area for a polygon using the given paths
 * @param {Array} paths - Array of {lat, lng} points forming the polygon
 * @returns {Object} - Area calculations in different units
 */
export const getPolygonArea = (geometry) => {
    // Handle circle case
    if (geometry && geometry.center && geometry.radius) {
        return getCircleArea(geometry.radius);
    }
    
    // Handle missing or invalid geometry
    if (!geometry || !Array.isArray(geometry) || geometry.length < 3) {
        console.warn('Invalid polygon geometry', geometry);
        return { areaMeters: 0, areaFeet: 0, areaAcres: 0 };
    }
    
    // Use Shoelace formula (Gauss's area formula) to calculate area
    let area = 0;
    for (let i = 0; i < geometry.length; i++) {
        const j = (i + 1) % geometry.length;
        
        // Ensure we have valid lat/lng values
        if (!geometry[i].lat || !geometry[j].lng) {
            continue;
        }
        
        area += geometry[i].lat * geometry[j].lng;
        area -= geometry[j].lat * geometry[i].lng;
    }
    area = Math.abs(area) / 2;
    
    // Convert to square meters (rough approximation for small areas)
    // For more accuracy, use a proper geodesic area calculation
    const areaMeters = area * 111320 * 111320;
    
    // Convert to square feet (1 sq meter = 10.7639 sq feet)
    const areaFeet = areaMeters * 10.7639;
    
    // Convert to acres (1 acre = 43,560 sq feet)
    const areaAcres = areaFeet / 43560;
    
    return {
        areaMeters: areaMeters,
        areaFeet: areaFeet,
        areaAcres: areaAcres
    };
};

/**
 * Calculates area in square meters for a circle
 * @param {number} radius - Radius of the circle in meters
 * @returns {object} - Area in different units
 */
export const getCircleArea = (radius) => {
    if (!radius || radius <= 0) return { areaMeters: 0, areaFeet: 0, areaAcres: 0 };
    
    // Calculate area using πr²
    const areaMeters = Math.PI * Math.pow(radius, 2);
    
    // Convert to square feet (1 sq meter = 10.7639 sq feet)
    const areaFeet = areaMeters * 10.7639;
    
    // Convert to acres (1 acre = 43,560 sq feet)
    const areaAcres = areaFeet / 43560;
    
    return {
        areaMeters: areaMeters,
        areaFeet: areaFeet,
        areaAcres: areaAcres
    };
};

/**
 * Creates a GeoJSON representation of a circle
 * @param {object} center - {lat, lng} of circle center
 * @param {number} radius - Radius in meters
 * @param {number} numPoints - Number of points to approximate the circle (default: 64)
 * @returns {object} - GeoJSON FeatureCollection with polygon feature
 */
export const circleToGeoJSON = (center, radius, numPoints = 64) => {
    if (!center || !radius) return null;
    
    const coordinates = [];
    const angularDistance = 2 * Math.PI / numPoints;
    
    for (let i = 0; i < numPoints; i++) {
        const angle = angularDistance * i;
        
        // Convert polar coordinates to cartesian
        // Note: We're using a simple approximation here - for more accuracy,
        // the Haversine formula should be used for the earth's curvature
        const lat = center.lat + (radius / 111320) * Math.cos(angle);
        const lng = center.lng + (radius / (111320 * Math.cos(center.lat * (Math.PI / 180)))) * Math.sin(angle);
        
        coordinates.push([lng, lat]); // GeoJSON uses [lng, lat] format
    }
    
    // Close the polygon by repeating the first point
    coordinates.push(coordinates[0]);
    
    return {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [coordinates]
            },
            properties: {
                radius: radius,
                center: [center.lng, center.lat]
            }
        }]
    };
};

/**
 * Helper to get geometry from a Leaflet layer
 * Enhanced to handle circles and other layer types properly
 */
// Enhanced getGeometryFromLayer function
export const getGeometryFromLayer = (layer) => {
    try {
        // Handle L.Circle
        if (layer instanceof L.Circle) {
            return {
                type: 'circle',
                center: {
                    lat: layer.getLatLng().lat,
                    lng: layer.getLatLng().lng
                },
                radius: layer.getRadius()
            };
        }
        
        // Handle L.Marker
        if (layer instanceof L.Marker) {
            return { 
                type: 'marker',
                latlng: layer.getLatLng() 
            };
        }
        
        // Handle L.Polygon, L.Rectangle, L.Polyline
        if (layer instanceof L.Polygon || layer instanceof L.Rectangle || layer instanceof L.Polyline) {
            let latlngs = layer.getLatLngs();
            
            console.log('Raw latlngs from layer:', latlngs);
            
            // Handle multi-polygons (nested arrays)
            if (latlngs[0] && Array.isArray(latlngs[0])) {
                // Flatten one level if needed
                latlngs = latlngs[0];
                console.log('Flattened latlngs:', latlngs);
            }
            
            return { 
                type: layer instanceof L.Polygon ? 'polygon' : 
                      layer instanceof L.Rectangle ? 'rectangle' : 'polyline',
                latlngs: latlngs 
            };
        }
        
        console.warn('Layer is not a recognized Leaflet shape type');
        return null;
    } catch (e) {
        console.error("Error getting geometry from layer:", e);
        console.error("Layer type:", layer?.constructor?.name);
        return null;
    }
};
export const getGeometryFromLayerOld = (layer) => {
    try {
        // Handle L.Circle
        if (layer instanceof L.Circle) {
            return {
                type: 'circle',
                center: {
                    lat: layer.getLatLng().lat,
                    lng: layer.getLatLng().lng
                },
                radius: layer.getRadius()
            };
        }
        
        // Handle L.Marker
        if (layer instanceof L.Marker) {
            return { 
                type: 'marker',
                latlng: layer.getLatLng() 
            };
        }
        
        // Handle L.Polygon, L.Rectangle, L.Polyline
        if (layer instanceof L.Polygon || layer instanceof L.Rectangle || layer instanceof L.Polyline) {
            let latlngs = layer.getLatLngs();
            
            // Handle multi-polygons (nested arrays)
            if (latlngs[0] && Array.isArray(latlngs[0])) {
                // Flatten one level if needed
                latlngs = latlngs[0];
            }
            
            return { 
                type: layer instanceof L.Polygon ? 'polygon' : 
                      layer instanceof L.Rectangle ? 'rectangle' : 'polyline',
                latlngs: latlngs 
            };
        }
    } catch (e) {
        console.error("Error getting geometry from layer:", e, layer);
    }
    
    console.warn("Unknown layer type for geometry extraction:", layer?.constructor?.name);
    return null;
};

/**
 * Calculates perimeter for a polygon using the given paths
 * @param {Array} paths - Array of {lat, lng} points forming the polygon
 * @returns {Object} - Perimeter calculations in different units
 */
export const getPolygonPerimeter = (paths) => {
    // Handle missing or invalid geometry
    if (!paths || !Array.isArray(paths) || paths.length < 3) {
        console.warn('Invalid polygon paths for perimeter calculation', paths);
        return { perimeterMeters: 0, perimeterFeet: 0 };
    }
    
    let perimeter = 0;
    for (let i = 0; i < paths.length; i++) {
        const j = (i + 1) % paths.length;
        
        // Ensure we have valid lat/lng values
        if (!paths[i].lat || !paths[i].lng || !paths[j].lat || !paths[j].lng) {
            continue;
        }
        
        // Calculate distance between points using Haversine formula for accuracy
        const dLat = (paths[j].lat - paths[i].lat) * Math.PI / 180;
        const dLon = (paths[j].lng - paths[i].lng) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(paths[i].lat * Math.PI / 180) * Math.cos(paths[j].lat * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = 6371000 * c; // Earth radius in meters (6371 km) * c
        
        perimeter += distance;
    }
    
    // Convert to feet (1 meter = 3.28084 feet)
    const perimeterFeet = perimeter * 3.28084;
    
    return {
        perimeterMeters: perimeter,
        perimeterFeet: perimeterFeet
    };
};

/**
 * Enhanced getPolygonArea to include perimeter calculation
 * @param {Array} paths - Array of {lat, lng} points forming the polygon
 * @returns {Object} - Area and perimeter calculations in different units
 */
export const enhancedGetPolygonArea = (paths) => {
    // Handle circle case 
    if (paths && paths.center && paths.radius) {
        const circleArea = getCircleArea(paths.radius);
        const circumference = {
            perimeterMeters: 2 * Math.PI * paths.radius,
            perimeterFeet: 2 * Math.PI * paths.radius * 3.28084
        };
        
        return {
            ...circleArea,
            perimeterMeters: circumference.perimeterMeters,
            perimeterFeet: circumference.perimeterFeet
        };
    }
    
    // Handle missing or invalid geometry
    if (!paths || !Array.isArray(paths) || paths.length < 3) {
        console.warn('Invalid polygon paths for area calculation', paths);
        return { 
            areaMeters: 0, 
            areaFeet: 0, 
            areaAcres: 0,
            perimeterMeters: 0,
            perimeterFeet: 0
        };
    }
    
    // Use Shoelace formula (Gauss's area formula) to calculate area
    let area = 0;
    for (let i = 0; i < paths.length; i++) {
        const j = (i + 1) % paths.length;
        
        // Ensure we have valid lat/lng values
        if (!paths[i].lat || !paths[j].lng) {
            continue;
        }
        
        area += paths[i].lat * paths[j].lng;
        area -= paths[j].lat * paths[i].lng;
    }
    area = Math.abs(area) / 2;
    
    // Convert to square meters (rough approximation for small areas)
    const areaMeters = area * 111320 * 111320;
    
    // Convert to square feet (1 sq meter = 10.7639 sq feet)
    const areaFeet = areaMeters * 10.7639;
    
    // Convert to acres (1 acre = 43,560 sq feet)
    const areaAcres = areaFeet / 43560;
    
    // Calculate perimeter using the helper function
    const perimeter = getPolygonPerimeter(paths);
    
    return {
        areaMeters: areaMeters,
        areaFeet: areaFeet,
        areaAcres: areaAcres,
        perimeterMeters: perimeter.perimeterMeters,
        perimeterFeet: perimeter.perimeterFeet
    };
};